import { useState, useRef, useEffect, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { arrayMove, useSortable } from "@dnd-kit/sortable";
import {
  Upload,
  FolderOpen,
  X,
  GripVertical,
  Loader2,
  Sparkles,
  Search,
  ImageIcon,
} from "lucide-react";
import { api, uploadFormData, type ApiResponse } from "@/api/client";
import {
  MEDIA_FOLDERS,
  type MediaResponse,
  type MediaFolder,
  type AltSuggestion,
} from "@ckgo2/shared/types";
import { useMedia } from "@/hooks/useMedia";
import { DndCSS, type DndTransform } from "@/lib/dndHelpers";
import DraggableList from "@/components/common/DraggableList";
import DuplicateWarning from "@/components/media/DuplicateWarning";
import AlbumTree from "@/components/media/AlbumTree";

type SingleProps = {
  mode?: "single";
  value: string | null | undefined;
  onChange: (id: string | null) => void;
  folder?: MediaFolder;
  label?: string;
  className?: string;
  /** Render as a controlled modal only (no inline thumb/trigger). Used by the Tiptap editor. */
  hideTrigger?: boolean;
  /** Controlled open state (only meaningful with hideTrigger). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

type MultipleProps = {
  mode: "multiple" | "gallery";
  value: string[] | undefined;
  onChange: (ids: string[]) => void;
  folder?: MediaFolder;
  label?: string;
  className?: string;
};

type Props = SingleProps | MultipleProps;

function Thumb({ id, onRemove }: { id: string; onRemove?: () => void }) {
  const { data } = useMedia(id);
  const media = data?.data;
  return (
    <div className="group relative aspect-square overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-800">
      {media ? (
        <img src={media.thumbUrl} alt={media.alt} className="h-full w-full object-cover" />
      ) : (
        <div className="flex h-full w-full items-center justify-center text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      )}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="bg-error-500/90 hover:bg-error-500 absolute right-1 top-1 rounded p-1 text-white opacity-0 transition group-hover:opacity-100"
          aria-label="Remove"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

/**
 * Drag-reorderable thumbnail for multiple/gallery mode. Order is meaningful —
 * the first item is the gallery cover — so editors can sort via the drag handle
 * (pointer + keyboard, both supplied by DraggableList's sensors). Index badge
 * makes the cover/position explicit.
 */
function SortableThumb({
  id,
  index,
  onRemove,
}: {
  id: string;
  index: number;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  const { data } = useMedia(id);
  const media = data?.data;

  const style = {
    transform: DndCSS.Transform.toString(transform as DndTransform | null),
    transition,
    zIndex: isDragging ? 50 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group relative aspect-square overflow-hidden rounded-lg border-2 bg-gray-100 dark:bg-gray-800 ${
        isDragging ? "border-primary-500 shadow-lg" : "border-transparent"
      }`}
    >
      {media ? (
        <img src={media.thumbUrl} alt={media.alt} className="h-full w-full object-cover" />
      ) : (
        <div className="flex h-full w-full items-center justify-center text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      )}

      <button
        type="button"
        className="absolute left-1 top-1 cursor-grab touch-none rounded bg-white/90 p-1 text-gray-600 opacity-0 shadow transition-opacity group-hover:opacity-100 dark:bg-gray-800/90 dark:text-gray-300"
        aria-label="Drag to reorder"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>

      <button
        type="button"
        onClick={onRemove}
        className="bg-error-500/90 hover:bg-error-500 absolute right-1 top-1 rounded p-1 text-white opacity-0 transition group-hover:opacity-100"
        aria-label="Remove"
      >
        <X className="h-3.5 w-3.5" />
      </button>

      <div className="absolute bottom-1 left-1 rounded bg-black/60 px-1.5 py-0.5 text-xs font-medium text-white">
        {index + 1}
      </div>
    </div>
  );
}

export default function MediaPicker(props: Props) {
  const { folder = "general", label, className = "" } = props;
  const isMultiple = props.mode === "multiple" || props.mode === "gallery";
  const single = !isMultiple ? (props as SingleProps) : null;
  const hideTrigger = single?.hideTrigger ?? false;
  const controlledOpen = single?.open;
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = useCallback(
    (next: boolean) => {
      if (single?.onOpenChange) single.onOpenChange(next);
      setInternalOpen(next);
    },
    [single],
  );

  const selectedIds: string[] = isMultiple
    ? ((props as MultipleProps).value ?? [])
    : (props as SingleProps).value
      ? [(props as SingleProps).value as string]
      : [];

  const handleSelect = useCallback(
    (id: string) => {
      if (isMultiple) {
        const cur = (props as MultipleProps).value ?? [];
        if (!cur.includes(id)) (props as MultipleProps).onChange([...cur, id]);
      } else {
        (props as SingleProps).onChange(id);
        setOpen(false);
      }
    },
    [isMultiple, props, setOpen],
  );

  const handleRemove = (id: string) => {
    if (isMultiple) {
      (props as MultipleProps).onChange(
        ((props as MultipleProps).value ?? []).filter((x) => x !== id),
      );
    } else {
      (props as SingleProps).onChange(null);
    }
  };

  const handleReorder = (oldIndex: number, newIndex: number) => {
    (props as MultipleProps).onChange(arrayMove(selectedIds, oldIndex, newIndex));
  };

  // Controlled modal-only mode (Tiptap editor): render just the modal.
  if (hideTrigger) {
    return open ? (
      <MediaModal
        folder={folder}
        selectedIds={selectedIds}
        multiple={isMultiple}
        onSelect={handleSelect}
        onClose={() => setOpen(false)}
      />
    ) : null;
  }

  return (
    <div className={className}>
      {label && (
        <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
          {label}
        </label>
      )}

      {selectedIds.length > 0 ? (
        isMultiple ? (
          <DraggableList itemIds={selectedIds} strategy="grid" onReorder={handleReorder}>
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
              {selectedIds.map((id, index) => (
                <SortableThumb key={id} id={id} index={index} onRemove={() => handleRemove(id)} />
              ))}
              <button
                type="button"
                onClick={() => setOpen(true)}
                className="flex aspect-square items-center justify-center rounded-lg border-2 border-dashed border-gray-300 text-gray-400 hover:border-gray-400 dark:border-gray-600"
              >
                <Upload className="h-6 w-6" />
              </button>
            </div>
          </DraggableList>
        ) : (
          <div className="max-w-xs">
            {selectedIds.map((id) => (
              <Thumb key={id} id={id} onRemove={() => handleRemove(id)} />
            ))}
          </div>
        )
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex aspect-video w-full max-w-xs flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 p-6 text-gray-500 transition-colors hover:border-gray-400 dark:border-gray-600"
        >
          <ImageIcon className="mb-2 h-8 w-8" />
          <span className="text-sm">Select or upload</span>
        </button>
      )}

      {!isMultiple && selectedIds.length > 0 && (
        <button type="button" onClick={() => setOpen(true)} className="btn-ghost mt-2 text-xs">
          <FolderOpen className="mr-1 inline h-3.5 w-3.5" />
          Change
        </button>
      )}

      {open && (
        <MediaModal
          folder={folder}
          selectedIds={selectedIds}
          multiple={isMultiple}
          onSelect={handleSelect}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

interface ModalProps {
  folder: MediaFolder;
  selectedIds: string[];
  multiple: boolean;
  onSelect: (id: string) => void;
  onClose: () => void;
}

function MediaModal({ folder, selectedIds, multiple, onSelect, onClose }: ModalProps) {
  const [tab, setTab] = useState<"library" | "upload">("library");
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[85vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-white shadow-xl dark:bg-gray-900">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-gray-700">
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => setTab("library")}
              className={`rounded-md px-3 py-1.5 text-sm font-medium ${tab === "library" ? "bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300" : "text-gray-500"}`}
            >
              Library
            </button>
            <button
              type="button"
              onClick={() => setTab("upload")}
              className={`rounded-md px-3 py-1.5 text-sm font-medium ${tab === "upload" ? "bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300" : "text-gray-500"}`}
            >
              Upload
            </button>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {tab === "library" ? (
            <LibraryTab
              folder={folder}
              selectedIds={selectedIds}
              multiple={multiple}
              onSelect={onSelect}
            />
          ) : (
            <UploadTab folder={folder} onUploaded={onSelect} onDone={() => setTab("library")} />
          )}
        </div>
      </div>
    </div>
  );
}

function LibraryTab({
  folder,
  selectedIds,
  multiple,
  onSelect,
}: {
  folder: MediaFolder;
  selectedIds: string[];
  multiple: boolean;
  onSelect: (id: string) => void;
}) {
  const [q, setQ] = useState("");
  const [activeFolder, setActiveFolder] = useState<MediaFolder | "">(folder);
  const [albumId, setAlbumId] = useState<string | null>(null);
  const [debouncedQ, setDebouncedQ] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const { data, isLoading } = useQuery({
    queryKey: ["media-library", activeFolder, albumId, debouncedQ],
    queryFn: () => {
      const params = new URLSearchParams({ limit: "60" });
      if (activeFolder) params.set("folder", activeFolder);
      if (albumId) params.set("folderId", albumId);
      if (debouncedQ) params.set("q", debouncedQ);
      return api.get<ApiResponse<{ data: MediaResponse[] }>>(`/api/admin/upload?${params}`);
    },
  });
  const items = data?.data?.data ?? [];

  return (
    <div className="flex gap-3">
      <aside className="hidden w-44 shrink-0 overflow-y-auto sm:block">
        <AlbumTree selectedId={albumId} onSelect={setAlbumId} />
      </aside>
      <div className="flex-1">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search alt, tags, albums…"
              className="w-full rounded-lg border border-gray-300 py-2 pl-8 pr-3 text-sm dark:border-gray-600 dark:bg-gray-800"
            />
          </div>
          <select
            value={activeFolder}
            onChange={(e) => setActiveFolder(e.target.value as MediaFolder | "")}
            className="rounded-lg border border-gray-300 px-2 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
          >
            <option value="">All folders</option>
            {MEDIA_FOLDERS.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
        </div>
        {isLoading ? (
          <div className="flex h-40 items-center justify-center text-gray-400">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <p className="py-10 text-center text-sm text-gray-500">No media found.</p>
        ) : (
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
            {items.map((m) => {
              const isSel = selectedIds.includes(m.id);
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => onSelect(m.id)}
                  title={m.alt || m.originalName || m.id}
                  className={`relative aspect-square overflow-hidden rounded-lg border-2 ${isSel ? "border-primary-500" : "border-transparent"}`}
                >
                  <img src={m.thumbUrl} alt={m.alt} className="h-full w-full object-cover" />
                  {isSel && multiple && (
                    <div className="bg-primary-500 absolute right-1 top-1 h-4 w-4 rounded-full" />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

interface PendingFile {
  id: string;
  file: File;
  preview: string;
  alt: string;
  title: string;
  tags: string[];
  description: string;
  suggesting: boolean;
  uploading: boolean;
  error?: string;
  duplicates: MediaResponse[];
  dupChecked: boolean;
  dupDismissed: boolean;
}

let pendingFileCounter = 0;
const nextPendingId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `pf-${++pendingFileCounter}`;

// AI suggest hits LLaVA + Mistral server-side per file. Drop 25 files in
// parallel and Workers AI rate-limits. Bounded fan-out matches the server's
// own concurrency posture for /multiple uploads.
const AI_SUGGEST_CONCURRENCY = 3;

/** Run tasks with a bounded parallel limit; resolves once all settle. */
async function runWithConcurrency(
  tasks: Array<() => Promise<unknown>>,
  limit: number,
): Promise<void> {
  let next = 0;
  const workers = Array.from({ length: Math.min(limit, tasks.length) }, async () => {
    while (next < tasks.length) {
      const i = next++;
      try {
        await tasks[i]();
      } catch {
        /* per-task suggest() already catches and surfaces error per-file */
      }
    }
  });
  await Promise.all(workers);
}

function UploadTab({
  folder,
  onUploaded,
  onDone,
}: {
  folder: MediaFolder;
  onUploaded: (id: string) => void;
  onDone: () => void;
}) {
  const [files, setFiles] = useState<PendingFile[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  // Keep a live ref of pending files so async handlers (resolved by id) read the
  // latest list rather than a stale closure, and so unmount cleanup can revoke
  // all outstanding object URLs.
  const filesRef = useRef<PendingFile[]>([]);
  filesRef.current = files;

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // Revoke any object URLs still alive on unmount to avoid leaks.
      filesRef.current.forEach((f) => URL.revokeObjectURL(f.preview));
    };
  }, []);

  // Remove a pending file by stable id and revoke its preview object URL.
  const removeById = useCallback((id: string) => {
    setFiles((prev) => {
      const target = prev.find((f) => f.id === id);
      if (target) URL.revokeObjectURL(target.preview);
      return prev.filter((f) => f.id !== id);
    });
  }, []);

  const update = (id: string, patch: Partial<PendingFile>) =>
    setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, ...patch } : f)));

  // Check whether a near-duplicate already exists; rows are resolved by stable id
  // since the list can shift while checks run.
  const checkDuplicates = useCallback(async (id: string, file: File) => {
    try {
      const fd = new FormData();
      fd.append("file", file);
      const json = await uploadFormData<{ data?: { similar?: MediaResponse[] } }>(
        "/api/admin/upload/check-duplicates",
        fd,
      );
      const similar = json.data?.similar ?? [];
      setFiles((prev) =>
        prev.map((f) => (f.id === id ? { ...f, duplicates: similar, dupChecked: true } : f)),
      );
    } catch {
      setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, dupChecked: true } : f)));
    }
  }, []);

  // Prefill alt/title/tags/description from a single AI vision call. `force`
  // distinguishes the two callers: addFiles passes force=false (only fill empty
  // fields — never clobber admin typing during the initial drop), the Generate
  // button passes force=true (admin explicitly asked for an AI override; replace
  // whatever's there with the new suggestion).
  const suggest = useCallback(async (id: string, force = false) => {
    const f = filesRef.current.find((pf) => pf.id === id);
    if (!f) return;
    update(id, { suggesting: true });
    try {
      const fd = new FormData();
      fd.append("file", f.file);
      const json = await uploadFormData<{ data?: Partial<AltSuggestion> }>(
        "/api/admin/upload/suggest-alt",
        fd,
      );
      const ai = json.data ?? {};
      setFiles((prev) =>
        prev.map((pf) =>
          pf.id !== id
            ? pf
            : force
              ? {
                  // Force mode: AI's response wins. For tags use `??` (not
                  // `?.length`): an AI response that includes `tags: []` is an
                  // explicit "no tags" answer the admin asked for. Keep pf only
                  // when the AI omitted the field entirely.
                  ...pf,
                  alt: ai.alt || pf.alt,
                  title: ai.title || pf.title,
                  tags: ai.tags ?? pf.tags,
                  description: ai.description || pf.description,
                }
              : {
                  ...pf,
                  alt: pf.alt || ai.alt || "",
                  title: pf.title || ai.title || "",
                  tags: pf.tags.length ? pf.tags : (ai.tags ?? []),
                  description: pf.description || ai.description || "",
                },
        ),
      );
    } finally {
      update(id, { suggesting: false });
    }
  }, []);

  const addFiles = (list: FileList | null) => {
    if (!list) return;
    const imgs = Array.from(list).filter((f) => f.type.startsWith("image/"));
    const next: PendingFile[] = imgs.map((file) => ({
      id: nextPendingId(),
      file,
      preview: URL.createObjectURL(file),
      alt: "",
      title: "",
      tags: [],
      description: "",
      suggesting: false,
      uploading: false,
      duplicates: [],
      dupChecked: false,
      dupDismissed: false,
    }));
    setFiles((prev) => [...prev, ...next]);
    // Dedup checks fan out freely (cheap SHA-256 + DB lookup). AI suggest is
    // expensive (LLaVA + Mistral ~5-9s) — cap concurrency so a 25-file drop
    // doesn't fire 25 parallel Workers AI calls and trip rate limits.
    next.forEach((pf) => void checkDuplicates(pf.id, pf.file));
    void runWithConcurrency(
      next.map((pf) => () => suggest(pf.id)),
      AI_SUGGEST_CONCURRENCY,
    );
  };

  const uploadOne = async (id: string) => {
    const f = filesRef.current.find((pf) => pf.id === id);
    if (!f) return;
    // Empty alt is fine: the server re-runs AI vision and falls back to a
    // humanised filename if needed. Sending a client-side fallback here would
    // skip that AI pass and lock in the bad name.
    update(id, { uploading: true, error: undefined });
    try {
      const fd = new FormData();
      fd.append("file", f.file);
      if (f.alt.trim()) fd.append("alt", f.alt.trim());
      if (f.title.trim()) fd.append("title", f.title.trim());
      fd.append("folder", folder);
      // Always send `tags` and `description` (even empty): explicit empty tells
      // the server the admin saw the AI suggestion and chose to clear it. Omitting
      // the fields would let the server re-run AI and silently undo the admin's
      // choice — bug parity with the providedTags contract on the server.
      fd.append("tags", JSON.stringify(f.tags));
      fd.append("description", f.description.trim());
      const json = await uploadFormData<{ data?: MediaResponse; message?: string }>(
        "/api/admin/upload",
        fd,
      );
      if (!json.data) throw new Error(json.message || "Upload failed");
      // Complete cleanup + cache invalidation BEFORE notifying the parent, which
      // in single-mode closes the modal (and unmounts this subtree). Sequencing
      // this way prevents state updates on an unmounted component.
      removeById(id);
      queryClient.invalidateQueries({ queryKey: ["media-library"] });
      onUploaded(json.data.id);
    } catch (err) {
      if (mountedRef.current)
        update(id, {
          uploading: false,
          error: err instanceof Error ? err.message : "Upload failed",
        });
    }
  };

  return (
    <div>
      <div
        onDrop={(e) => {
          e.preventDefault();
          addFiles(e.dataTransfer.files);
        }}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => inputRef.current?.click()}
        className="mb-4 flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 p-8 text-gray-500 hover:border-gray-400 dark:border-gray-600"
      >
        <Upload className="mb-2 h-8 w-8" />
        <p className="text-sm">Drop images here or click to browse</p>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => addFiles(e.target.files)}
        />
      </div>

      {files.length > 0 && (
        <div className="space-y-3">
          {files.map((f) => (
            <div
              key={f.id}
              className="flex gap-3 rounded-lg border border-gray-200 p-2 dark:border-gray-700"
            >
              <img src={f.preview} alt="" className="h-20 w-20 rounded object-cover" />
              <div className="flex-1 space-y-1">
                <div className="flex gap-2">
                  <input
                    value={f.alt}
                    maxLength={125}
                    onChange={(e) => update(f.id, { alt: e.target.value })}
                    placeholder="Alt text (auto-filled by AI if blank)"
                    className="flex-1 rounded border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-800"
                  />
                  <button
                    type="button"
                    onClick={() => suggest(f.id, true)}
                    disabled={f.suggesting}
                    className="btn-ghost whitespace-nowrap text-xs"
                  >
                    {f.suggesting ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Sparkles className="mr-1 inline h-3.5 w-3.5" />
                    )}
                    Generate
                  </button>
                </div>
                <input
                  value={f.title}
                  maxLength={60}
                  onChange={(e) => update(f.id, { title: e.target.value })}
                  placeholder="Title (short — drives the SEO filename + image sitemap)"
                  className="w-full rounded border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-800"
                />
                <input
                  value={f.tags.join(", ")}
                  onChange={(e) =>
                    update(f.id, {
                      tags: e.target.value
                        .split(",")
                        .map((t) => t.trim().toLowerCase())
                        .filter(Boolean),
                    })
                  }
                  placeholder="Tags (comma-separated, for search)"
                  className="w-full rounded border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-800"
                />
                <textarea
                  value={f.description}
                  onChange={(e) => update(f.id, { description: e.target.value })}
                  placeholder="Description (longer, for keyword search)"
                  rows={2}
                  className="w-full rounded border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-800"
                />
                {f.duplicates.length > 0 && !f.dupDismissed && (
                  <DuplicateWarning
                    matches={f.duplicates}
                    onUseExisting={(m) => {
                      removeById(f.id);
                      queryClient.invalidateQueries({ queryKey: ["media-library"] });
                      onUploaded(m.id);
                    }}
                    onDismiss={() => update(f.id, { dupDismissed: true })}
                  />
                )}
                {f.error && <p className="text-error-600 text-xs">{f.error}</p>}
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => removeById(f.id)}
                    className="btn-ghost text-xs"
                  >
                    Remove
                  </button>
                  <button
                    type="button"
                    onClick={() => uploadOne(f.id)}
                    disabled={f.uploading || f.suggesting}
                    title={f.suggesting ? "Waiting for AI suggestion to finish" : undefined}
                    className="btn-primary text-xs"
                  >
                    {f.uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Upload"}
                  </button>
                </div>
              </div>
            </div>
          ))}
          <button type="button" onClick={onDone} className="btn-ghost w-full text-sm">
            Done — back to library
          </button>
        </div>
      )}
    </div>
  );
}
